/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: API Architecture
 * PURPOSE: Centralized API endpoint configuration with type-safe path builders
 *
 * NEW PATTERNS IMPLEMENTED:
 * - Path Builder Pattern: Functional endpoint builders via apiPath
 * - SSR Safety: Browser checks prevent SSR hydration mismatches
 *
 * KEY FEATURES:
 * - Comprehensive endpoint definitions for all API domains
 * - WebSocket URL construction with protocol detection (ws/wss)
 * - Environment-specific configuration (prod/dev)
 * - Feature flags and UI configuration
 *
 * RELATED FILES:
 * - src/lib/services/api/pathBuilder.ts
 * - src/lib/services/api/client.ts
 */

import { browser } from '$app/environment';
import { devLog } from '$lib/utils/dev';
import { apiPath } from '$lib/services/api/pathBuilder';
/**
 * API endpoints configuration
 */
export const API_CONFIG = {
	/**
	 * Base URL for API endpoints
	 * Returns empty string during SSR, actual base URL in browser
	 */
	BASE_URL: browser ? apiPath.getBaseUrl() : '',

	FEEDBACK: {
		BASE: () => apiPath.path('/feedback'),
		CREATE: () => apiPath.path('/feedback/'),
		LIST: () => apiPath.path('/feedback/'),
		DETAILS: (id: string) => apiPath.path('/feedback/:id', { id }),
		UPDATE: (id: string) => apiPath.path('/feedback/:id', { id }),
		COMMENTS: (id: string) => apiPath.path('/feedback/:id/comments', { id }),
		STATUS: (id: string) => apiPath.path('/feedback/:id/status', { id }),
		DASHBOARD_STATS: () => apiPath.path('/feedback/dashboard/stats'),
		MY_FEEDBACK: () => apiPath.path('/feedback/my-feedback'),
		PUBLIC: () => apiPath.path('/feedback/public'),
		MARK_READ: (id: string) => apiPath.path('/feedback/:id/read', { id }),
		VOTERS: (id: string) => apiPath.path('/feedback/:id/voters', { id }),
		VOTE: (id: string) => apiPath.path('/feedback/:id/vote', { id }),
		// Use feedback-specific attachment endpoint (bypasses org check for admin access)
		ATTACHMENT_DOWNLOAD: (feedbackId: string) =>
			apiPath.path('/feedback/:id/attachments', { id: feedbackId })
	},

	FILE_UPLOAD: {
		SINGLE: () => apiPath.path('/uploads/file'),
		MULTIPLE: () => apiPath.path('/uploads/files'),
		LIST: () => apiPath.path('/uploads/files'),
		TAGS: () => apiPath.path('/uploads/file/tags'),
		UPDATE_TAGS: () => apiPath.path('/uploads/file/tags'),
		DELETE: () => apiPath.path('/uploads/file')
	},

	/**
	 * Health-related endpoints
	 */
	HEALTH: {
		/**
		 * Unified health check endpoint
		 */
		CHECK: () => apiPath.path('/health/'),
		STARTUP_STATUS: () => apiPath.path('/health/startup-status')
	},

	USERS: {
		/**
		 * Current user profile endpoint
		 */
		ME: () => apiPath.path('/users/me'),

		/**
		 * Update current user profile
		 */
		UPDATE_PROFILE: () => apiPath.path('/users/me'),

		/**
		 * Get all user settings (preferences, notifications, UI)
		 */
		SETTINGS: () => apiPath.path('/users/me/settings'),

		/**
		 * Update UI settings
		 */
		SETTINGS_UI: () => apiPath.path('/users/me/settings/ui'),

		/**
		 * Update user preferences
		 */
		SETTINGS_PREFERENCES: () => apiPath.path('/users/me/settings/preferences'),

		/**
		 * Update user password
		 */
		PASSWORD: () => apiPath.path('/users/me/password'),

		/**
		 * Update notification settings
		 */
		SETTINGS_NOTIFICATIONS: () => apiPath.path('/users/me/settings/notifications'),

		/**
		 * Get fresh permissions from backend
		 */
		PERMISSIONS: () => apiPath.path('/users/me/permissions')
	},

	/**
	 * Authentication endpoints
	 */
	AUTH: {
		/**
		 * Login endpoint
		 */
		LOGIN: () => apiPath.path('/auth/login'),

		/**
		 * Microsoft auth endpoint
		 */
		MICROSOFT: () => apiPath.path('/auth/microsoft'),

		/**
		 * Logout endpoint
		 */
		LOGOUT: () => apiPath.path('/auth/logout'),

		/**
		 * Get current user endpoint
		 */
		ME: () => apiPath.path('/auth/me/info'),

		/**
		 * Refresh token endpoint
		 */
		REFRESH: () => apiPath.path('/auth/refresh'),

		/**
		 * Password reset request endpoint
		 */
		PASSWORD_RESET_REQUEST: () => apiPath.path('/auth/password-reset/request'),

		/**
		 * Validate password reset token endpoint
		 */
		PASSWORD_RESET_VALIDATE: () => apiPath.path('/auth/password-reset/validate-token'),

		/**
		 * Confirm password reset endpoint
		 */
		PASSWORD_RESET_CONFIRM: () => apiPath.path('/auth/password-reset/confirm')
	},

	/**
	 * Alert-related endpoints
	 */
	ALERTS: {
		/**
		 * Unified endpoint for alert actions (approve, reject, resolve)
		 */
		ACTION: () => apiPath.path('/operations/alerts/action'),

		/**
		 * Endpoint for approving alerts
		 * @param alertId The ID of the alert to approve
		 */
		APPROVE: (alertId: string) => apiPath.path('/operations/alerts/:alertId/approve', { alertId }),

		/**
		 * Endpoint for rejecting alerts
		 * @param alertId The ID of the alert to reject
		 */
		REJECT: (alertId: string) => apiPath.path('/operations/alerts/:alertId/reject', { alertId }),

		/**
		 * Endpoint for getting alert details
		 * @param alertId The ID of the alert to get details for
		 */
		DETAILS: (alertId: string) => apiPath.path('/operations/alerts/:alertId', { alertId }),

		/**
		 * Endpoint for listing alerts
		 */
		LIST: () => apiPath.path('/operations/alerts'),

		/**
		 * Endpoint for the Archived Alerts view (rejected/resolved, on-demand).
		 * Gated by ALERTS:VIEW_ARCHIVED; scope-aware via the X-AMOS-Scope header.
		 */
		ARCHIVE: () => apiPath.path('/operations/alerts/archive'),

		INSIGHTS: (alertId: string) =>
			apiPath.path('/operations/alerts/:alertId/insights', { alertId }),

		/**
		 * Endpoint for creating work orders from alerts
		 */
		CREATE_WORK_ORDER: () => apiPath.path('/operations/work-orders/create-from-alerts')
	},

	/**
	 * Work order-related endpoints
	 */
	WORK_ORDERS: {
		/**
		 * Endpoint for creating work orders
		 */
		CREATE: () => apiPath.path('/operations/alerts/create/work-order'),

		/**
		 * Endpoint for getting work order details
		 * @param workOrderId The ID of the work order to get details for
		 */
		DETAILS: (workOrderId: string) =>
			apiPath.path('/operations/work-orders/:workOrderId', { workOrderId }),

		UPDATE: (workOrderId: string) =>
			apiPath.path('/operations/work-orders/:workOrderId', { workOrderId }),

		/**
		 * Delete a work order with options for linked alerts
		 * @param workOrderId The ID of the work order to delete
		 * Query param alert_action: 'revert' | 'hide'
		 */
		DELETE: (workOrderId: string) =>
			apiPath.path('/operations/work-orders/:workOrderId', { workOrderId }),

		/**
		 * Revert specific alerts from a work order back to approved
		 * @param workOrderId The ID of the work order
		 */
		REVERT_ALERTS: (workOrderId: string) =>
			apiPath.path('/operations/work-orders/:workOrderId/revert-alerts', { workOrderId }),

		/**
		 * Un-dispatch a work order: revert its still-owned alerts to approved and
		 * cancel the work order. Backend: POST /api/v1/operations/work-orders/{id}/undispatch
		 */
		UNDISPATCH: (workOrderId: string) =>
			apiPath.path('/operations/work-orders/:workOrderId/undispatch', { workOrderId }),

		/**
		 * Get alerts linked to a work order
		 * @param workOrderId The ID of the work order
		 */
		ALERTS: (workOrderId: string) =>
			apiPath.path('/operations/work-orders/:workOrderId/alerts', { workOrderId }),

		/**
		 * Endpoint for listing work orders
		 */
		LIST: () => apiPath.path('/operations/work-orders'),

		TIMELINE: (workOrderId: string) =>
			apiPath.path('/operations/work-orders/:workOrderId/timeline', { workOrderId }),

		/**
		 * Dynamics 365 Purchase Orders for a work order
		 * @param dynamicsId The Dynamics 365 work order GUID
		 */
		DYNAMICS_PURCHASE_ORDERS: (dynamicsId: string) =>
			apiPath.path('/operations/work-orders/dynamics/:dynamicsId/purchase-orders', { dynamicsId }),

		/**
		 * Dynamics 365 PO Bills (invoices) for a work order
		 * @param dynamicsId The Dynamics 365 work order GUID
		 */
		DYNAMICS_BILLS: (dynamicsId: string) =>
			apiPath.path('/operations/work-orders/dynamics/:dynamicsId/bills', { dynamicsId }),

		/**
		 * Paginated Dynamics 365 work orders (server-side filtering/sorting)
		 */
		DYNAMICS_PAGINATED: () => apiPath.path('/operations/work-orders/dynamics'),

		// Scheduling endpoints (manager-facing)
		SCHEDULING: {
			/**
			 * Bulk update work order scheduling
			 */
			BULK_UPDATE: () => apiPath.path('/operations/work-orders/scheduling/bulk'),

			/**
			 * Get scheduled work orders for a specific user
			 * @param userId The ID of the user
			 */
			USER_SCHEDULE: (userId: string) =>
				apiPath.path('/operations/work-orders/scheduling/user/:userId', { userId }),

			/**
			 * Get scheduled work orders for the current user
			 */
			MY_SCHEDULE: () => apiPath.path('/operations/work-orders/scheduling/my-schedule'),

			/**
			 * Get scheduled work orders for a specific vendor
			 * @param vendorId The ID of the vendor
			 */
			VENDOR_SCHEDULE: (vendorId: string) =>
				apiPath.path('/operations/work-orders/scheduling/vendor/:vendorId', { vendorId }),

			/**
			 * Get work orders by tech type (vendor or company)
			 */
			BY_TECH_TYPE: () => apiPath.path('/operations/work-orders/scheduling/by-tech-type')
		},

		// NEW: File management endpoints
		FILES: {
			/**
			 * Upload a single file to a work order
			 */
			UPLOAD_SINGLE: () => apiPath.path('/operations/work-orders/upload-file'),

			/**
			 * Upload multiple files to a work order
			 */
			UPLOAD_MULTIPLE: () => apiPath.path('/operations/work-orders/files/bulk-upload'),

			/**
			 * Search files across work orders
			 */
			SEARCH: () => apiPath.path('/operations/work-orders/files/search'),

			/**
			 * Get all files for a work order by work order number
			 * @param woNumber The work order number
			 */
			BY_NUMBER: (woNumber: string) =>
				apiPath.path('/operations/work-orders/by-number/:woNumber/files', { woNumber }),

			/**
			 * Get all files for a specific work order
			 * @param workOrderId The ID of the work order
			 */
			LIST: (workOrderId: string) =>
				apiPath.path('/operations/work-orders/:workOrderId/files', { workOrderId }),

			/**
			 * Download a file by ID
			 * @param woNumber The work order number
			 * @param fileId The file ID
			 */
			DOWNLOAD_BY_ID: (woNumber: string, fileId: string) =>
				apiPath.path('/operations/work-orders/:woNumber/files/:fileId/download', {
					woNumber,
					fileId
				}),

			/**
			 * Download a file by name (query parameter-based)
			 * @param woNumber The work order number
			 */
			DOWNLOAD_BY_NAME: (woNumber: string) =>
				apiPath.path('/operations/work-orders/:woNumber/files/download', { woNumber }),

			/**
			 * Download a file by URL (query parameter-based)
			 */
			DOWNLOAD_BY_URL: () => apiPath.path('/operations/work-orders/files/download'),

			/**
			 * Get file metadata (HEAD request endpoint)
			 * @param woNumber The work order number
			 * @param fileId The file ID
			 */
			METADATA: (woNumber: string, fileId: string) =>
				apiPath.path('/operations/work-orders/:woNumber/files/:fileId', { woNumber, fileId }),

			/**
			 * Delete a file from a work order
			 */
			DELETE: () => apiPath.path('/operations/work-orders/files/delete'),

			/**
			 * Get available tags for work order files
			 */
			TAGS_SUMMARY: () => apiPath.path('/operations/work-orders/files/tags/summary'),

			/**
			 * Update file tags
			 */
			UPDATE_TAGS: () => apiPath.path('/operations/work-orders/files/tag/update')
		},

		// Technician scheduling endpoints
		TECH_SCHEDULING: {
			/**
			 * Schedule work orders to technicians
			 */
			SCHEDULE: () => apiPath.path('/operations/work-orders/schedule'),

			/**
			 * Get technician's schedule
			 * @param techId The ID of the technician
			 */
			TECH_SCHEDULE: (techId: string) =>
				apiPath.path('/operations/work-orders/tech/:techId/schedule', { techId }),

			/**
			 * Update work order priority
			 * @param workOrderId The ID of the work order
			 */
			UPDATE_PRIORITY: (workOrderId: string) =>
				apiPath.path('/operations/work-orders/:workOrderId/priority', { workOrderId }),

			/**
			 * Start a repair session on a work order
			 * Backend: POST /api/v1/technician/portal/work-orders/{id}/start-repair
			 * @param workOrderId The ID of the work order
			 */
			START: (workOrderId: string) =>
				apiPath.path('/technician/portal/work-orders/:workOrderId/start-repair', { workOrderId }),

			/**
			 * Pause a repair session on a work order
			 * Backend: POST /api/v1/technician/portal/work-orders/{id}/pause-repair
			 * @param workOrderId The ID of the work order
			 */
			PAUSE: (workOrderId: string) =>
				apiPath.path('/technician/portal/work-orders/:workOrderId/pause-repair', { workOrderId }),

			/**
			 * Resume a paused repair session
			 * Backend: POST /api/v1/technician/portal/work-orders/{id}/resume-repair
			 * @param workOrderId The ID of the work order
			 */
			RESUME: (workOrderId: string) =>
				apiPath.path('/technician/portal/work-orders/:workOrderId/resume-repair', { workOrderId }),

			/**
			 * End a repair session on a work order
			 * Backend: POST /api/v1/technician/portal/work-orders/{id}/end-repair
			 * @param workOrderId The ID of the work order
			 */
			END: (workOrderId: string) =>
				apiPath.path('/technician/portal/work-orders/:workOrderId/end-repair', { workOrderId }),

			/**
			 * Get vendor schedule (legacy support)
			 * @param vendorId The ID of the vendor
			 */
			VENDOR_SCHEDULE: (vendorId: string) =>
				apiPath.path('/operations/work-orders/vendor/:vendorId/schedule', { vendorId }),

			/**
			 * Unschedule a work order (remove tech assignments and scheduling)
			 * Backend: POST /api/v1/operations/work-orders/{id}/unschedule
			 * @param workOrderId The ID of the work order
			 */
			UNSCHEDULE: (workOrderId: string) =>
				apiPath.path('/operations/work-orders/:workOrderId/unschedule', { workOrderId })
		},

		// NEW: Manual work order creation endpoints
		MANUAL: {
			/**
			 * Get site selection data for manual work order creation
			 */
			SITES_SELECTION_DATA: () => apiPath.path('/operations/manual/sites/selection-data'),

			/**
			 * Get components for a specific site
			 * @param assetId The asset ID of the site
			 */
			SITE_COMPONENTS: (assetId: string) =>
				apiPath.path('/operations/manual/sites/:assetId/components', { assetId }),

			/**
			 * Create manual work order
			 */
			CREATE_WORK_ORDER: () => apiPath.path('/operations/manual/work-orders/create')
		}
	},

	/**
	 * SharePoint file discovery endpoints
	 */
	SHAREPOINT: {
		DISCOVER: () => apiPath.path('/sharepoint/discover'),
		SEARCH: () => apiPath.path('/sharepoint/search')
	},

	/**
	 * WebSocket endpoints
	 * Note: WebSocket URLs are constructed dynamically based on BASE_URL
	 */
	WEBSOCKETS: {
		/**
		 * Main WebSocket connection URL that's safe for SSR
		 */
		CONNECT: () => {
			// Check if we're in a browser environment
			if (!browser) {
				// Return a placeholder during SSR
				return 'ws://placeholder-during-ssr';
			}

			// Extract API domain from BASE_URL using apiPath.getBaseUrl()
			const baseUrl = apiPath.getBaseUrl();
			if (!baseUrl) {
				// Fallback for empty base URL (relative paths)
				return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
			}

			try {
				const apiUrlObj = new URL(baseUrl);
				const apiDomain = apiUrlObj.host;
				const wsProtocol = apiUrlObj.protocol === 'https:' ? 'wss:' : 'ws:';
				return `${wsProtocol}//${apiDomain}/ws`;
			} catch {
				// Fallback
				return baseUrl.includes('api.aspenpower-amos.com')
					? 'wss://api.aspenpower-amos.com/ws'
					: `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
			}
		},

		// Chat WebSocket removed - feature not needed

		/**
		 * Notifications WebSocket connection URL
		 * @param clientId The client ID for the connection
		 * @param token Optional authentication token
		 */
		NOTIFICATIONS: (clientId: string, token?: string) => {
			// Check if we're in a browser environment
			if (!browser) {
				// Return a placeholder during SSR
				return 'ws://placeholder-during-ssr';
			}

			// Extract API domain from BASE_URL using apiPath.getBaseUrl()
			const baseUrl = apiPath.getBaseUrl();
			if (!baseUrl) {
				// Fallback for empty base URL (relative paths)
				const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/v1/ws/notifications/${clientId}`;
				return token ? `${wsUrl}?token=${encodeURIComponent(token)}` : wsUrl;
			}

			try {
				const apiUrlObj = new URL(baseUrl);
				const apiDomain = apiUrlObj.host;
				const wsProtocol = apiUrlObj.protocol === 'https:' ? 'wss:' : 'ws:';
				const wsUrl = `${wsProtocol}//${apiDomain}/api/v1/ws/notifications/${clientId}`;
				return token ? `${wsUrl}?token=${encodeURIComponent(token)}` : wsUrl;
			} catch {
				// Fallback
				const wsUrl = baseUrl.includes('api.aspenpower-amos.com')
					? `wss://api.aspenpower-amos.com/api/v1/ws/notifications/${clientId}`
					: `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/v1/ws/notifications/${clientId}`;
				return token ? `${wsUrl}?token=${encodeURIComponent(token)}` : wsUrl;
			}
		},

		/**
		 * Feedback WebSocket connection URL
		 * @param clientId The client ID for the connection
		 * @param token Optional authentication token
		 */
		FEEDBACK: (clientId: string, token?: string) => {
			// Check if we're in a browser environment
			if (!browser) {
				// Return a placeholder during SSR
				return 'ws://placeholder-during-ssr';
			}

			// Extract API domain from BASE_URL using apiPath.getBaseUrl()
			const baseUrl = apiPath.getBaseUrl();
			if (!baseUrl) {
				// Fallback for empty base URL (relative paths)
				const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/v1/ws/feedback/${clientId}`;
				return token ? `${wsUrl}?token=${encodeURIComponent(token)}` : wsUrl;
			}

			try {
				const apiUrlObj = new URL(baseUrl);
				const apiDomain = apiUrlObj.host;
				const wsProtocol = apiUrlObj.protocol === 'https:' ? 'wss:' : 'ws:';
				const wsUrl = `${wsProtocol}//${apiDomain}/api/v1/ws/feedback/${clientId}`;
				return token ? `${wsUrl}?token=${encodeURIComponent(token)}` : wsUrl;
			} catch {
				// Fallback
				const wsUrl = baseUrl.includes('api.aspenpower-amos.com')
					? `wss://api.aspenpower-amos.com/api/v1/ws/feedback/${clientId}`
					: `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/v1/ws/feedback/${clientId}`;
				return token ? `${wsUrl}?token=${encodeURIComponent(token)}` : wsUrl;
			}
		}
	},

	AUDIT: {
		// Main endpoints
		LOGS: () => apiPath.path('/audit/logs'),
		CHART_DATA: () => apiPath.path('/audit/chart-data'),
		SUMMARY_STATS: () => apiPath.path('/audit/stats/summary'),
		UNIFIED_LOGS: () => apiPath.path('/audit/logs/unified'),
		PERFORMERS: () => apiPath.path('/audit/performers')
	},

	SECURITY_AUDIT: {
		LOGS: () => apiPath.path('/security-audit/logs'),
		CHART_DATA: () => apiPath.path('/security-audit/chart-data'),
		SUMMARY_STATS: () => apiPath.path('/security-audit/stats/summary'),
		UNIFIED_LOGS: () => apiPath.path('/security-audit/logs/unified'),
		PERFORMERS: () => apiPath.path('/security-audit/performers'),
		ENTITY_TIMELINE: (targetType: string, targetId: string) =>
			apiPath.path(`/security-audit/${targetType}/${targetId}`)
	},

	/**
	 * AI Insights endpoints
	 */
	AI_INSIGHTS: {
		/**
		 * Get AI-powered analysis for a specific alert
		 * @param alertId The ID of the alert to analyze
		 */
		ALERT_ANALYSIS: (alertId: string) => apiPath.path('/ai-insights/alerts/:alertId', { alertId })
	},

	/**
	 * AI Auto-Triage endpoints
	 */
	AI_TRIAGE: {
		DECISIONS: () => apiPath.path('/ai-triage/decisions'),
		DECISION: (alertFlagId: string) =>
			apiPath.path('/ai-triage/decisions/:alertFlagId', { alertFlagId }),
		ACCEPT: (alertFlagId: string) =>
			apiPath.path('/ai-triage/decisions/:alertFlagId/accept', { alertFlagId }),
		REJECT: (alertFlagId: string) =>
			apiPath.path('/ai-triage/decisions/:alertFlagId/reject', { alertFlagId }),
		OVERRIDE: (alertFlagId: string) =>
			apiPath.path('/ai-triage/decisions/:alertFlagId/override', { alertFlagId }),
		STATS: () => apiPath.path('/ai-triage/stats'),
		ANALYTICS: () => apiPath.path('/ai-triage/analytics'),
		CONFIG: () => apiPath.path('/ai-triage/config')
	},

	/**
	 * Unified Alert Analysis endpoints (replaces dual auto-triage + approval-insights)
	 */
	AI_ANALYSIS: {
		ANALYZE: () => apiPath.path('/ai/analyze-alert'),
		GET: (alertFlagId: string) => apiPath.path('/ai/alert-analysis/:alertFlagId', { alertFlagId }),
		BATCH: () => apiPath.path('/ai/alert-analysis/batch'),
		DECISIONS: () => apiPath.path('/ai/alert-analysis/decisions'),
		DEEP: (alertFlagId: string) =>
			apiPath.path('/ai/alert-analysis/:alertFlagId/deep', { alertFlagId }),
		DEEP_AGENT: (alertFlagId: string) =>
			apiPath.path('/ai/alert-analysis/:alertFlagId/deep-agent', { alertFlagId }),
		DEEP_AGENT_STREAM: (alertFlagId: string) =>
			apiPath.path('/ai/alert-analysis/:alertFlagId/deep-agent/stream', { alertFlagId }),
		ACCEPT: (alertFlagId: string) =>
			apiPath.path('/ai/alert-analysis/:alertFlagId/accept', { alertFlagId }),
		REJECT: (alertFlagId: string) =>
			apiPath.path('/ai/alert-analysis/:alertFlagId/reject', { alertFlagId }),
		OVERRIDE: (alertFlagId: string) =>
			apiPath.path('/ai/alert-analysis/:alertFlagId/override', { alertFlagId }),
		STATS: () => apiPath.path('/ai/alert-analysis/stats')
	},

	/**
	 * Daily Fleet Reports (P0 — automated daily report)
	 */
	REPORTS: {
		LIST_DAILY: () => apiPath.path('/reports/daily'),
		GET_DAILY: (id: string) => apiPath.path('/reports/daily/:id', { id }),
		GENERATE_DAILY: () => apiPath.path('/reports/daily/generate')
	},

	/**
	 * AMOS agent endpoints backed by the AEGIS external-agent bridge.
	 */
	AGENT: {
		CHAT: () => apiPath.path('/agent/chat'),
		PAGE_SNAPSHOTS: () => apiPath.path('/agent/page-snapshots'),
		PENDING_UI_COMMANDS: () => apiPath.path('/agent/ui-commands/pending'),
		UI_COMMAND_COMPLETE: (commandId: string) =>
			apiPath.path('/agent/ui-commands/:commandId/complete', { commandId }),
		ATTENTION_ITEMS: () => apiPath.path('/agent/artifacts/attention-items'),
		LATEST_BRIEFING: () => apiPath.path('/agent/artifacts/briefings/latest'),
		REPORTS: () => apiPath.path('/agent/artifacts/reports'),
		ALERT_DECISION_DRAFTS: () => apiPath.path('/agent/alert-decision-drafts'),
		ALERT_DECISION_DRAFT_ACCEPT: (draftId: string) =>
			apiPath.path('/agent/alert-decision-drafts/:draftId/accept', { draftId }),
		ALERT_DECISION_DRAFT_REJECT: (draftId: string) =>
			apiPath.path('/agent/alert-decision-drafts/:draftId/reject', { draftId }),
		ALERT_DECISION_DRAFT_OVERRIDE: (draftId: string) =>
			apiPath.path('/agent/alert-decision-drafts/:draftId/override', { draftId })
	},

	/**
	 * AI Archive endpoints
	 */
	AI_ARCHIVE: {
		RECOMMENDATIONS: () => apiPath.path('/ai-archive/recommendations'),
		STATS: () => apiPath.path('/ai-archive/recommendations/stats'),
		SCAN: () => apiPath.path('/ai-archive/scan'),
		SCAN_STATUS: () => apiPath.path('/ai-archive/scan/status'),
		ARCHIVE: () => apiPath.path('/ai-archive/archive'),
		RESTORE: () => apiPath.path('/ai-archive/restore'),
		DISMISS: () => apiPath.path('/ai-archive/dismiss'),
		ARCHIVED_ALERTS: () => apiPath.path('/ai-archive/archived/alerts'),
		ARCHIVED_WORK_ORDERS: () => apiPath.path('/ai-archive/archived/work-orders')
	},

	/**
	 * Analytics endpoints
	 */
	ANALYTICS: {
		/**
		 * Get work order preview summary with AI analysis
		 */
		WORK_ORDER_PREVIEW: () => apiPath.path('/analytics/work-order-preview/summary'),
		/**
		 * Get alert approval insights with AI-powered recommendation
		 */
		ALERT_APPROVAL_INSIGHTS: () => apiPath.path('/analytics/alert-approval-insights/analyze'),
		/** EXPERIMENTAL: Energy Prediction */
		ENERGY_FORECAST_PREDICT: (assetId: string) =>
			apiPath.path('/analytics/energy-forecast/predict/:assetId', { assetId }),
		ENERGY_FORECAST_STATUS: (assetId: string) =>
			apiPath.path('/analytics/energy-forecast/status/:assetId', { assetId }),
		ENERGY_FORECAST_SITES: apiPath.path('/analytics/energy-forecast/sites'),
		ENERGY_FORECAST_HISTORY: (assetId: string) =>
			apiPath.path('/analytics/energy-forecast/history/:assetId', { assetId }),
		ENERGY_FORECAST_PREDICT_RATIO: (assetId: string) =>
			apiPath.path('/analytics/energy-forecast/predict-ratio/:assetId', { assetId }),
		ENERGY_FORECAST_RATIO_STATUS: (assetId: string) =>
			apiPath.path('/analytics/energy-forecast/ratio-status/:assetId', { assetId }),
		ENERGY_FORECAST_RATIO_SITES: () => apiPath.path('/analytics/energy-forecast/ratio-sites'),
		ENERGY_FORECAST_RATIO_HISTORY: (assetId: string) =>
			apiPath.path('/analytics/energy-forecast/ratio-history/:assetId', { assetId }),
		ENERGY_FORECAST_PREDICTION_HISTORY: (siteId: string) =>
			apiPath.path('/analytics/energy-forecast/prediction-history/:siteId', { siteId }),
		ENERGY_FORECAST_FLEET_SUMMARY: () => apiPath.path('/analytics/energy-forecast/fleet-summary'),
		/** Pro Forma vs Forecast vs Actual — daily series, fleet-wide (Phase 2). */
		ENERGY_FORECAST_PERFORMANCE_SERIES: () =>
			apiPath.path('/analytics/energy-forecast/performance-series'),
		/** Pro Forma vs Forecast vs Actual — daily series, single site (Phase 2). */
		SITE_INSIGHTS_PERFORMANCE_SERIES: (assetId: string) =>
			apiPath.path('/analytics/site-insights/:assetId/performance-series', { assetId }),
		/** Batch ML Failure Prediction */
		BATCH_FAILURE_PREDICTION: {
			/** Start batch prediction for alerts */
			START: () => apiPath.path('/analytics/batch-failure-prediction/start'),
			/** Get all cached predictions */
			CACHED: () => apiPath.path('/analytics/batch-failure-prediction/cached'),
			/** Cancel a running batch */
			CANCEL: (batchId: string) =>
				apiPath.path('/analytics/batch-failure-prediction/:batchId', { batchId }),
			/** Clear all cached predictions */
			CLEAR_CACHE: () => apiPath.path('/analytics/batch-failure-prediction/cache'),
			/** ML Scanner admin controls */
			SCANNER_STATUS: () => apiPath.path('/analytics/batch-failure-prediction/scanner/status'),
			SCANNER_START: () => apiPath.path('/analytics/batch-failure-prediction/scanner/start'),
			SCANNER_STOP: () => apiPath.path('/analytics/batch-failure-prediction/scanner/stop'),
			SCANNER_TRIGGER: () => apiPath.path('/analytics/batch-failure-prediction/scanner/trigger')
		}
	},
	LOOKUP: {
		USERS: () => apiPath.path('/lookup/users'),
		DEPARTMENTS: () => apiPath.path('/lookup/departments'),
		ROLES: () => apiPath.path('/lookup/roles'),
		ORGANIZATIONS: () => apiPath.path('/lookup/organizations'),
		GENERIC: () => apiPath.path('/lookup/generic')
	},

	/**
	 * Admin Management API Endpoints
	 */
	ADMIN: {
		// Organizations
		ORGANIZATIONS: {
			LIST: () => apiPath.path('/admin/organizations'),
			DETAILS: (id: string) => apiPath.path('/admin/organizations/:id', { id }),
			CREATE: () => apiPath.path('/admin/organizations'),
			UPDATE: (id: string) => apiPath.path('/admin/organizations/:id', { id }),
			DELETE: (id: string) => apiPath.path('/admin/organizations/:id', { id })
		},

		// Departments
		DEPARTMENTS: {
			LIST: () => apiPath.path('/admin/departments'),
			DETAILS: (id: string) => apiPath.path('/admin/departments/:id', { id }),
			CREATE: () => apiPath.path('/admin/departments'),
			UPDATE: (id: string) => apiPath.path('/admin/departments/:id', { id }),
			DELETE: (id: string) => apiPath.path('/admin/departments/:id', { id })
		},

		// Roles
		ROLES: {
			LIST: () => apiPath.path('/admin/roles'),
			DETAILS: (id: string) => apiPath.path('/admin/roles/:id', { id }),
			CREATE: () => apiPath.path('/admin/roles'),
			UPDATE: (id: string) => apiPath.path('/admin/roles/:id', { id }),
			DELETE: (id: string) => apiPath.path('/admin/roles/:id', { id }),
			PERMISSIONS: (roleId: string) => apiPath.path('/admin/roles/:roleId/permissions', { roleId }),
			ASSIGN_PERMISSIONS: (roleId: string) =>
				apiPath.path('/admin/roles/:roleId/permissions', { roleId }),
			REMOVE_PERMISSION: (roleId: string, permissionId: string) =>
				apiPath.path('/admin/roles/:roleId/permissions/:permissionId', { roleId, permissionId })
		},

		// Permissions
		PERMISSIONS: {
			LIST: () => apiPath.path('/admin/permissions'),
			DETAILS: (id: string) => apiPath.path('/admin/permissions/:id', { id }),
			CREATE: () => apiPath.path('/admin/permissions'),
			UPDATE: (id: string) => apiPath.path('/admin/permissions/:id', { id }),
			DELETE: (id: string) => apiPath.path('/admin/permissions/:id', { id })
		},

		// API Keys
		API_KEYS: {
			LIST: () => apiPath.path('/admin/api-keys'),
			DETAILS: (id: string) => apiPath.path('/admin/api-keys/:id', { id }),
			CREATE: () => apiPath.path('/admin/api-keys'),
			UPDATE: (id: string) => apiPath.path('/admin/api-keys/:id', { id }),
			ROTATE: (id: string) => apiPath.path('/admin/api-keys/:id/rotate', { id }),
			REVOKE: (id: string) => apiPath.path('/admin/api-keys/:id/revoke', { id }),
			USAGE: (id: string) => apiPath.path('/admin/api-keys/:id/usage', { id })
		},

		AGENT_INTEGRATION: {
			DETAILS: () => apiPath.path('/admin/agent-integration'),
			UPDATE: () => apiPath.path('/admin/agent-integration'),
			HEALTH_CHECK: () => apiPath.path('/admin/agent-integration/health-check')
		},

		// Dashboard
		DASHBOARD: {
			STATS: () => apiPath.path('/admin/dashboard/stats')
		},

		// Monitoring
		MONITORING: {
			OVERVIEW: () => apiPath.path('/admin/monitoring/overview'),
			ERRORS: () => apiPath.path('/admin/monitoring/errors'),
			SLOW_REQUESTS: () => apiPath.path('/admin/monitoring/slow-requests')
		},

		// OPA
		OPA: {
			STATUS: () => apiPath.path('/admin/opa/status'),
			SYNC_PENDING: () => apiPath.path('/admin/opa/sync/pending'),
			SYNC_USER: (userId: string) => apiPath.path('/admin/opa/sync/user/:userId', { userId }),
			SYNC_ALL: () => apiPath.path('/admin/opa/sync/all'),
			FORCE_SYNC: () => apiPath.path('/admin/opa/sync/force')
		},

		// Users
		USERS: {
			LIST: () => apiPath.path('/admin/users'),
			DETAILS: (id: string) => apiPath.path('/admin/users/:id', { id }),
			CREATE: () => apiPath.path('/admin/users'),
			UPDATE: (id: string) => apiPath.path('/admin/users/:id', { id }),
			DELETE: (id: string) => apiPath.path('/admin/users/:id', { id }),
			RESET_PASSWORD: (userId: string) => apiPath.path('/admin/users/:userId/password', { userId }),
			UNLOCK: (userId: string) => apiPath.path('/admin/users/:userId/unlock', { userId }),
			ASSIGN_ROLE: (userId: string) => apiPath.path('/admin/users/:userId/roles', { userId }),
			REMOVE_ROLE: (userId: string, roleId: string) =>
				apiPath.path('/admin/users/:userId/roles/:roleId', { userId, roleId }),
			ASSIGN_DEPARTMENT: (userId: string) =>
				apiPath.path('/admin/users/:userId/departments', { userId }),
			REMOVE_DEPARTMENT: (userId: string, departmentId: string) =>
				apiPath.path('/admin/users/:userId/departments/:departmentId', { userId, departmentId })
		},

		// Components
		COMPONENTS: {
			LIST: () => apiPath.path('/admin/components'),
			DETAILS: (componentId: string) =>
				apiPath.path('/admin/components/:componentId', { componentId }),
			CREATE: () => apiPath.path('/admin/components'),
			UPDATE: (componentId: string) =>
				apiPath.path('/admin/components/:componentId', { componentId }),
			DELETE: (componentId: string) =>
				apiPath.path('/admin/components/:componentId', { componentId }),
			BULK_UPDATE: () => apiPath.path('/admin/components/bulk'),
			STATISTICS: () => apiPath.path('/admin/components/statistics'),
			CATEGORIES: () => apiPath.path('/admin/components/categories'),
			USER_ACCESS: () => apiPath.path('/admin/components/user-access')
		}
	},

	// Chat endpoints removed - feature not needed

	/**
	 * Technician Portal endpoints
	 */
	/**
	 * AlsoEnergy/PowerTrack API endpoints
	 */
	ALSOENERGY: {
		/**
		 * Get live data for a hardware component
		 * @param hardwareId The hardware ID from AlsoEnergy
		 */
		HARDWARE_DATA: (hardwareId: string) =>
			apiPath.path('/alsoenergy/hardware/:hardwareId/data', { hardwareId }),

		/**
		 * Check AlsoEnergy API health status
		 */
		HEALTH: () => apiPath.path('/alsoenergy/health'),

		/**
		 * Get all accessible sites
		 */
		SITES: () => apiPath.path('/alsoenergy/sites'),

		/**
		 * Get hardware inventory for a site
		 * @param siteId The AlsoEnergy site ID
		 */
		SITE_HARDWARE: (siteId: string) =>
			apiPath.path('/alsoenergy/sites/:siteId/hardware', { siteId }),

		/**
		 * Get site-level performance data
		 * @param siteId The AlsoEnergy site ID
		 */
		SITE_DATA: (siteId: string) => apiPath.path('/alsoenergy/sites/:siteId/data', { siteId })
	},

	TECHNICIAN: {
		/**
		 * Dashboard endpoints
		 */
		DASHBOARD: () => apiPath.path('/technician/dashboard'),
		DASHBOARD_WORK_ORDERS: () => apiPath.path('/technician/dashboard/work-orders'),

		/**
		 * Work order endpoints
		 */
		WORK_ORDERS: () => apiPath.path('/technician/work-orders'),
		WORK_ORDER_DETAILS: (workOrderId: string) =>
			apiPath.path('/technician/work-orders/:workOrderId', { workOrderId }),
		WORK_ORDER_STATUS: (workOrderId: string) =>
			apiPath.path('/technician/work-orders/:workOrderId/status', { workOrderId }),
		WORK_ORDER_COMMENTS: (workOrderId: string) =>
			apiPath.path('/technician/work-orders/:workOrderId/comments', { workOrderId }),
		WORK_ORDER_ALERTS: (workOrderId: string) =>
			apiPath.path('/technician/work-orders/:workOrderId/alerts', { workOrderId }),

		/**
		 * Location tracking endpoints
		 */
		LOCATION: () => apiPath.path('/technician/location'),
		LOCATION_HISTORY: () => apiPath.path('/technician/location/history'),
		ROUTE: () => apiPath.path('/technician/route'),

		/**
		 * Shift management endpoints
		 */
		SHIFT_START: () => apiPath.path('/technician/shifts/start'),
		SHIFT_END: () => apiPath.path('/technician/shifts/end'),
		SHIFT_CURRENT: () => apiPath.path('/technician/shifts/current'),
		SHIFT_HISTORY: () => apiPath.path('/technician/shifts/history'),

		/**
		 * Alert endpoints
		 */
		ALERT_LIVE_DATA: (alertId: string) =>
			apiPath.path('/technician/alerts/:alertId/live-data', { alertId }),
		ALERT_RESOLUTION: (alertId: string) =>
			apiPath.path('/technician/alerts/:alertId/resolution', { alertId }),

		/**
		 * Preferences endpoints
		 */
		PREFERENCES: () => apiPath.path('/technician/preferences')
	},

	/**
	 * Notification-related endpoints
	 */
	NOTIFICATIONS: {
		/**
		 * User notification endpoints
		 */
		/**
		 * Get user notifications
		 */
		LIST: () => apiPath.path('/notifications/'),

		/**
		 * Get unread notification count
		 */
		UNREAD_COUNT: () => apiPath.path('/notifications/unread-count'),

		/**
		 * Mark notification as read
		 * @param notificationId The ID of the notification
		 */
		MARK_READ: (notificationId: string) =>
			apiPath.path('/notifications/:notificationId/read', { notificationId }),

		/**
		 * Dismiss notification
		 * @param notificationId The ID of the notification
		 */
		DISMISS: (notificationId: string) =>
			apiPath.path('/notifications/:notificationId/dismiss', { notificationId }),

		/**
		 * Mark notification as actioned
		 * @param notificationId The ID of the notification
		 */
		MARK_ACTIONED: (notificationId: string) =>
			apiPath.path('/notifications/:notificationId/action', { notificationId }),

		/**
		 * Delete notification
		 * @param notificationId The ID of the notification
		 */
		DELETE: (notificationId: string) =>
			apiPath.path('/notifications/:notificationId', { notificationId }),

		/**
		 * Mark all notifications as read
		 */
		MARK_ALL_READ: () => apiPath.path('/notifications/mark-all-read'),

		/**
		 * Notification preferences endpoints
		 */
		PREFERENCES: {
			/**
			 * Get notification types
			 */
			TYPES: () => apiPath.path('/notifications/types'),

			/**
			 * Get user notification preferences
			 */
			LIST: () => apiPath.path('/notifications/preferences'),

			/**
			 * Update preference for specific notification type
			 * @param typeId The ID of the notification type
			 */
			UPDATE: (typeId: string) => apiPath.path('/notifications/preferences/:typeId', { typeId }),

			/**
			 * Update global notification preferences
			 */
			UPDATE_GLOBAL: () => apiPath.path('/notifications/preferences/global'),

			/**
			 * Set quiet hours
			 */
			QUIET_HOURS: () => apiPath.path('/notifications/preferences/quiet-hours')
		},

		/**
		 * Admin notification endpoints
		 */
		ADMIN: {
			/**
			 * Create system announcement
			 */
			ANNOUNCEMENTS: () => apiPath.path('/notifications/announcements'),

			/**
			 * Get notification statistics
			 */
			STATS: () => apiPath.path('/notifications/stats')
		}
	}
};

/**
 * Environment-specific configuration
 */
export const ENV_CONFIG = {
	/**
	 * Whether the application is running in production mode
	 */
	IS_PRODUCTION: import.meta.env.PROD || false,

	/**
	 * Environment name
	 */
	ENVIRONMENT: import.meta.env.MODE || 'development',

	/**
	 * API version
	 */
	API_VERSION: 'v1',

	/**
	 * Logging configuration
	 */
	LOGGING: {
		/**
		 * Whether to enable debug logging
		 */
		ENABLE_DEBUG: !import.meta.env.PROD
	}
};

/**
 * Feature flags
 */
export const FEATURES = {
	/**
	 * Whether to enable real-time updates
	 */
	ENABLE_REAL_TIME: true,

	/**
	 * Whether to enable offline mode
	 */
	ENABLE_OFFLINE_MODE: false
};

/**
 * UI configuration
 */
export const UI_CONFIG = {
	/**
	 * Pagination default page size
	 */
	DEFAULT_PAGE_SIZE: 10,

	/**
	 * Toast notification duration in milliseconds
	 */
	TOAST_DURATION: 5000
};

/**
 * Authentication Configuration
 */
export const AUTH_CONFIG = {
	/**
	 * Local storage key for auth data
	 */
	STORAGE_KEY: 'app_auth',

	/**
	 * Buffer time in seconds before token expiry to refresh
	 */
	TOKEN_EXPIRY_BUFFER: 300
};

/**
 * Microsoft Authentication Configuration
 */
export const MICROSOFT_CONFIG = {
	/**
	 * Microsoft client ID
	 */
	CLIENT_ID: import.meta.env.VITE_MICROSOFT_CLIENT_ID || '',

	/**
	 * Microsoft tenant ID
	 */
	TENANT_ID: import.meta.env.VITE_MICROSOFT_TENANT_ID || 'websocket',

	/**
	 * Redirect URI after Microsoft authentication
	 */
	REDIRECT_URI:
		import.meta.env.VITE_MICROSOFT_REDIRECT_URI || (browser ? window.location.origin : ''),

	/**
	 * Microsoft Graph API scopes
	 */
	SCOPES: ['User.Read']
};

/**
 * Application Configuration
 */
export const APP_CONFIG = {
	/**
	 * Application name
	 */
	NAME: 'Aspen Power',

	/**
	 * Application version
	 */
	VERSION: '1.0.0',

	/**
	 * Logo path
	 */
	LOGO_PATH: '/logos/AspenPower_Logo_Horizontal_Light.svg',

	/**
	 * Default theme
	 */
	DEFAULT_THEME: 'light'
};

/**
 * Export all configurations as a single object
 */
export const config = {
	API: API_CONFIG,
	ENV: ENV_CONFIG,
	FEATURES,
	UI: UI_CONFIG,
	AUTH: AUTH_CONFIG,
	MICROSOFT: MICROSOFT_CONFIG,
	APP: APP_CONFIG
};

// For debugging - make sure this is only logged in browser
if (browser) {
	devLog('🔗 API Configuration loaded:', {
		BASE_URL: apiPath.getBaseUrl(),
		API_VERSION: apiPath.getApiVersion(),
		HEALTH_CHECK: API_CONFIG.HEALTH.CHECK(),
		AUTH_LOGIN: API_CONFIG.AUTH.LOGIN()
	});
}

export default API_CONFIG;
